import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet, HelmetProvider } from "react-helmet-async";
import { useAuth } from "../../accounts/AuthProvider";
import { useSavedCharts } from "../../accounts/SavedChartsProvider";
import { chartErrorMessage, CHART_PAGE_SIZE, listCharts } from "../../accounts/chartRepository";
import { libraryLocations } from "../LibraryPage/libraryTree";
import classes from "./MyCharts.module.css";

const ChartList = () => {
  const { client, session } = useAuth();
  const saved = useSavedCharts();
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [refresh, setRefresh] = useState(0);
  const [state, setState] = useState({ items: [], total: 0, loading: true, error: "" });
  const [edit, setEdit] = useState(null);
  const operation = useRef(null);
  useEffect(() => () => operation.current?.abort(), []);
  useEffect(() => {
    const controller = new AbortController();
    setState(previous => ({ ...previous, loading: true, error: "" }));
    listCharts({ client, accessToken: session.access_token, signal: controller.signal }, page)
      .then(result => {
        if (!controller.signal.aborted) setState({ ...result, loading: false, error: "" });
      }).catch(error => {
        if (!controller.signal.aborted) setState({ items: [], total: 0, loading: false, error: chartErrorMessage(error) });
      });
    return () => controller.abort();
  }, [client, session.access_token, page, refresh]);

  const actOn = async (kind, item, path) => {
    operation.current?.abort();
    const controller = new AbortController();
    operation.current = controller;
    if (kind === "open") {
      const result = await saved.open(item.id, controller.signal);
      if (result && !controller.signal.aborted) navigate("/");
    } else {
      const result = await saved.changeSaved(kind, item, controller.signal, path);
      if (result && !controller.signal.aborted) { setEdit(null); setPage(0); setRefresh(value => value + 1); }
    }
  };

  return <>
    <div className={classes.actions}>
      <button disabled={state.loading || saved.busy} onClick={() => { setEdit(null); setRefresh(value => value + 1); }}>Refresh My Charts</button>
      <Link to="/">Return to your chart</Link>
    </div>
    {state.loading && <p role="status">Loading your charts…</p>}
    {(state.error || saved.error) && <p role="alert">{state.error || saved.error}</p>}
    {saved.message && <p role="status">{saved.message}</p>}
    {!state.loading && !state.error && state.total === 0 && <p>You have no saved charts yet. In Refine or Preview, choose Save chart set.</p>}
    {state.items.length > 0 && <div className={classes.listHeading} aria-hidden="true">
      <span>Chart</span><span>Library location</span><span>Actions</span>
    </div>}
    <ul className={classes.list} aria-label="Saved chart sets">
      {state.items.map(item => <li key={item.id} className={classes.item}>
        <article className={classes.chartRow} aria-label={item.title || "Untitled chart"}>
          <h2 title={item.title || "Untitled chart"}>{item.title || "Untitled chart"}</h2>
          <label className={classes.location}><span className={classes.locationLabel}>Library location</span>
            <select aria-label={`Library location for ${item.title || "Untitled chart"}`}
              value={item.library_path || ""} disabled={saved.busy || state.loading}
              onChange={event => actOn("move", item, event.target.value)}>
              {!libraryLocations.some(folder => folder.path === (item.library_path || "")) &&
                <option value={item.library_path}>{item.library_path} (folder unavailable; shown at root)</option>}
              {libraryLocations.map(folder => <option key={folder.path} value={folder.path}>{folder.label}</option>)}
            </select>
          </label>
          <div className={`${classes.actions} ${classes.rowActions}`}>
            <button disabled={saved.busy || state.loading} onClick={() => actOn("open", item)}>Edit</button>
            <button disabled={saved.busy || state.loading} onClick={() => actOn("duplicate", item)}>Duplicate</button>
            <button disabled={saved.busy || state.loading} onClick={() => setEdit({ id: item.id, kind: "delete" })}>Delete</button>
          </div>
          {edit?.id === item.id && <div className={classes.edit} role="group" aria-label="Confirm saved chart deletion">
            <p>Delete “{item.title || "Untitled chart"}” from My Charts? This cannot be undone.</p>
            <div className={classes.actions}><button disabled={saved.busy} onClick={() => actOn("delete", item)}>Delete permanently</button>
              <button disabled={saved.busy} onClick={() => setEdit(null)}>Cancel deletion</button></div>
          </div>}
        </article>
      </li>)}
    </ul>
    {!state.loading && state.total > 0 && <div className={classes.actions} aria-label="Chart list pages">
      <button disabled={page === 0 || saved.busy} onClick={() => { setEdit(null); setPage(value => value - 1); }}>Previous page</button>
      <span>Page {page + 1} of {Math.max(1, Math.ceil(state.total / CHART_PAGE_SIZE))}</span>
      <button disabled={(page + 1) * CHART_PAGE_SIZE >= state.total || saved.busy} onClick={() => { setEdit(null); setPage(value => value + 1); }}>Next page</button>
    </div>}
  </>;
};

const MyChartsPage = () => {
  const auth = useAuth();
  return <section className={classes.panel}>
    <HelmetProvider><Helmet><title>My Charts | Guitar Charts</title><meta name="robots" content="noindex, nofollow" /></Helmet></HelmetProvider>
    <h1>My Charts</h1>
    {!auth.configured ? <p>Accounts are not available on this version of the site yet.</p>
      : auth.loading ? <p role="status">Checking your account…</p>
      : !auth.session || auth.error ? <p><Link to="/account">Sign in</Link> to open your personal charts.</p>
      : <ChartList key={auth.session.user.id} />}
  </section>;
};

export default MyChartsPage;
